import React, { useState, useEffect } from "react";
import ContentShell from "components/ContentShell";
import { Box, Typography, Card, CardContent, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "hooks/useSnackbar";
import { SimpleTextInput } from "components/FormTextInput";
import { getDashboardSummary } from "services/reportsService";
import sessionHelper from "services/sessionHelper";
import { SimpleSelectInput } from "./FormSelectInput";
import {
  getCurrencyBasedData,
  getUserTimezone,
  serializeDateValue,
  startOfWeek,
  wrapWithAll,
} from "utils/miscUtils";
import { SimpleDateInput } from "./FormDateInput";
import {
  CurrencyOptions,
  DefaultCurrency,
  PBO_InitialValues,
} from "utils/constants";
import useFilterOptionsHook from "hooks/useFilterOptions";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "100%",
  },
  filterSection: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 24,
  },
  filterField: {
    width: 160,
  },
  field: {
    width: 180,
  },
  cardContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 8,
  },
  card: {
    width: 200,
    height: 100,
    borderRadius: 8,
    backgroundColor: "#f9f9f9",
    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
  },
  cardContent: {
    textAlign: "center",
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 8,
  },
  cardValue: {
    fontSize: 18,
    fontWeight: 700,
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 24,
    gap: 5,
    marginLeft: 10,
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

interface FilterSectionProps {
  handleChange?: ({ name, value }: { name: string; value: any }) => void;
  onSubmitClick?: () => void;
  onReset?: () => void;
  filterOptions: any;
}

const FilterSection = ({
  handleChange = () => { },
  onSubmitClick = () => { },
  onReset = () => { },
  filterOptions,
}: FilterSectionProps) => {
  const classes = useStyles();
  const customStyle = { height: 30 };
  return (
    <Box className={classes.filterSection}>
      <Box className={classes.field} style={{ marginLeft: "0px" }}>
        <SimpleDateInput
          label="From:"
          id="startTime"
          name="startTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.startTime}
          onChange={(date) =>
            handleChange({ name: "startTime", value: date })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleDateInput
          label="To:"
          id="endTime"
          name="endTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.endTime}
          onChange={(date) =>
            handleChange({ name: "endTime", value: date })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleSelectInput
          name="platform"
          label="Platform"
          placeholdertext="Select Platforms"
          options={wrapWithAll(sessionHelper.platformOptions)}
          value={filterOptions.platform}
          onChange={(e) =>
            handleChange({ name: "platform", value: e?.target?.value })
          }
          customStyle={customStyle}
        />
      </Box>
      {![null, "all"].includes(filterOptions.platform) && (
        <Box className={classes.field}>
          <SimpleSelectInput
            name="operator"
            label="Operator"
            options={wrapWithAll(
              sessionHelper.getFilteredOperators({
                platformIds: [filterOptions.platform],
              })
            )}
            value={filterOptions.operator}
            placeholder="Select Operators"
            onChange={(e) =>
              handleChange({ name: "operator", value: e?.target?.value })
            }
            customStyle={customStyle}
          />
        </Box>
      )}
      {![null, "all"].includes(filterOptions.operator) && (
        <Box className={classes.field}>
          <SimpleSelectInput
            name="brand"
            label="Brand"
            options={wrapWithAll(
              sessionHelper.getFilteredBrands({
                platformIds: [filterOptions.platform],
                operatorIds: [filterOptions.operator],
              })
            )}
            value={filterOptions.brand}
            onChange={({ target: { name, value } }) =>
              handleChange({ name, value })
            }
            customStyle={customStyle}
          />
        </Box>
      )}
      <Box className={classes.field}>
        <SimpleSelectInput
          name="currency"
          label="Currency"
          options={CurrencyOptions}
          value={filterOptions.currency}
          onChange={(e) =>
            handleChange({ name: "currency", value: e?.target?.value })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleTextInput
          onChange={({ target: { name, value } }) =>
            handleChange({ name, value })
          }
          customStyle={{ height: 30 }}
          type="text"
          label="External Player Id"
          name="externalPlayerId"
          value={filterOptions.externalPlayerId}
        />
      </Box>
      <Box className={classes.buttonContainer}>
        <Button className={classes.button} onClick={onSubmitClick}>
          Submit
        </Button>
        <Button className={classes.button} onClick={onReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};

const Dashboard = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [filteredData, setFilteredData] = useState(null);

  const {
    filterOptions,
    onChange: setFilterOptions,
    reset,
  } = useFilterOptionsHook({
    initialValues: {
      startTime: startOfWeek(),
      endTime: new Date(),
      currency: DefaultCurrency,
      externalPlayerId: null,
      ...PBO_InitialValues,
    },
  });

  const fetchDashboardSummary = async () => {
    setLoading(true);
    try {
      const { startTime, endTime, currency, ...rest } = filterOptions;
      const response = await getDashboardSummary({
        ...rest,
        startTime: serializeDateValue(startTime),
        endTime: serializeDateValue(endTime),
        timezone: getUserTimezone()
      });

      const _summary = response?.data?.overallSummary;
      const _data = getCurrencyBasedData([{ ..._summary }], currency);
      setSummary(_summary);
      setFilteredData(_data[0]);
    } catch (error) {
      showSnackbar(error?.message || "Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = ({ name, value }: any) => {
    setFilterOptions({ name, value });

    if (name === "currency") {
      const _data = getCurrencyBasedData(
        [{ ...summary }],
        filterOptions.currency
      );
      setFilteredData(_data[0]);
    }
  };

  const getSummaryCards = (d: any) => [
    { title: "Bet Count", value: d?.totalBetCount || 0 },
    { title: "Coin Count", value: d?.purchaseBetCount || 0 },
    { title: "Win Bet Count", value: d?.winBetCount || 0 },
    { title: "Lose Bet Count", value: d?.losingBetCount || 0 },
    { title: "Unique Player Count", value: d?.uniquePlayerCount || 0 },
    { title: "Total Bet Price", value: d?.totalBet },
    { title: "Total Coin Price", value: d?.purchaseTotalBet },
    { title: "Total Win Price", value: d?.totalWin },
    { title: "GGR", value: d?.grossGamingRevenue },
    { title: "RTP", value: d?.rtp },
  ];

  useEffect(() => {
    fetchDashboardSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentShell title="Dashboard Summary" loading={loading}>
      <Box className={classes.playerCardParent}>
        <FilterSection
          filterOptions={filterOptions}
          handleChange={handleFilterChange}
          onSubmitClick={fetchDashboardSummary}
          onReset={reset}
        />
        <Box className={classes.cardContainer}>
          {getSummaryCards(filteredData)?.map((card, index) => (
            <Card key={index} className={classes.card}>
              <CardContent className={classes.cardContent}>
                <Typography className={classes.cardTitle}>
                  {card.title}
                </Typography>
                <Typography className={classes.cardValue}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </ContentShell>
  );
};

export default Dashboard;
